import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, merge, Observable, of, shareReplay, startWith, Subject, switchMap, take, timer } from 'rxjs';
import { map } from 'rxjs/operators';
import { Revisione53State } from '../store/revisione53.reducers';
import { Revisione53CoreSelectors } from '../store/selectors/core.selectors';
import { Utilities } from '@core/helpers/utilities';

@Injectable()
export class FormSettlingService {
    private store = inject<Store<Revisione53State>>(Store);

    readonly settled$: Observable<boolean> = this.store.select(Revisione53CoreSelectors.getMyVerbale).pipe(
        filter(Boolean),
        switchMap(() => merge(of(false), timer(Utilities.STORE_QUIET_MS).pipe(map(() => true)))),
        startWith(false),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: false })
    );

    private readonly _bulkRefresh$ = new Subject<void>();

    /**
     * True while a bulk operation (initial load or XML import) is still being validated; false once the store is quiet.
     * Unlike `settled$`, it re-enters the loading state ONLY on explicit bulk triggers (not on single-field edits),
     * so the navbar loading indicator shows on load/import but never flickers while the user types.
     */
    readonly settling$: Observable<boolean> = merge(of(void 0), this._bulkRefresh$).pipe(
        switchMap(() =>
            this.settled$.pipe(
                filter(Boolean),
                take(1),
                map(() => false),
                startWith(true)
            )
        ),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: false })
    );

    /** Signals a bulk store change (XML import) so `settling$` re-enters the loading state. */
    notifyBulkRefresh(): void {
        this._bulkRefresh$.next();
    }
}
